import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipboardList, ArrowLeft, CheckCircle2, Save } from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import WhsGate from '../../../components/whs/WhsGate';
import Button from '../../../components/ui/Button';
import whsService from '../../../services/whs';
import type { Inspection, InspectionResult, InspectionStatus } from '../../../types/whs';
import {
  Badge, INSPECTION_STATUS_LABEL, INSPECTION_STATUS_CLASS, INSPECTION_RESULT_LABEL,
} from '../../../components/whs/whsConstants';
import { useOrgTimezone, formatLocalDateInTz } from '../../../utils/timezone';

const RESULTS: InspectionResult[] = ['pass', 'fail', 'na'];

const RESULT_ACTIVE_CLASS: Record<InspectionResult, string> = {
  pass: 'bg-emerald-600 text-white border-emerald-600',
  fail: 'bg-red-600 text-white border-red-600',
  na: 'bg-neutral-500 text-white border-neutral-500',
};

const STATUS_OPTIONS: InspectionStatus[] = ['scheduled', 'in_progress', 'completed', 'cancelled'];

interface ItemEdit { result: InspectionResult; comment: string; }

function inspectorName(i: Inspection): string {
  const full = `${i.inspector_first_name || ''} ${i.inspector_last_name || ''}`.trim();
  return full || '—';
}

function WhsInspectionDetailInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const inspectionId = Number(id);

  const [inspection, setInspection] = useState<Inspection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [edits, setEdits] = useState<Record<number, ItemEdit>>({});
  const [summary, setSummary] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const res = await whsService.getInspection(inspectionId);
    if (res.success) {
      setInspection(res.data);
      setSummary(res.data.summary || '');
      const map: Record<number, ItemEdit> = {};
      for (const it of res.data.items) {
        map[it.id] = { result: it.result, comment: it.comment || '' };
      }
      setEdits(map);
    } else {
      setError(res.error.message);
    }
    setLoading(false);
  }, [inspectionId]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    if (!inspection) return [];
    const groups: Record<string, typeof inspection.items> = {};
    for (const it of inspection.items) {
      const key = it.category || '';
      (groups[key] ||= []).push(it);
    }
    return Object.entries(groups);
  }, [inspection]);

  const tally = useMemo(() => {
    let pass = 0, fail = 0, na = 0;
    for (const e of Object.values(edits)) {
      if (e.result === 'pass') pass++;
      else if (e.result === 'fail') fail++;
      else na++;
    }
    return { pass, fail, na };
  }, [edits]);

  const setResult = (itemId: number, result: InspectionResult) => {
    setEdits((prev) => ({ ...prev, [itemId]: { ...prev[itemId], result } }));
  };
  const setComment = (itemId: number, comment: string) => {
    setEdits((prev) => ({ ...prev, [itemId]: { ...prev[itemId], comment } }));
  };

  const saveResults = async () => {
    if (!inspection) return;
    setSaving(true);
    setError(null);
    const items = inspection.items.map((it) => ({
      id: it.id,
      result: edits[it.id]?.result ?? it.result,
      comment: edits[it.id]?.comment ?? it.comment,
    }));
    const res = await whsService.updateInspection(inspectionId, { items, summary });
    setSaving(false);
    if (res.success) await load();
    else setError(res.error.message);
  };

  const completeInspection = async () => {
    setSaving(true);
    setError(null);
    const res = await whsService.updateInspection(inspectionId, { status: 'completed' });
    setSaving(false);
    if (res.success) await load();
    else setError(res.error.message);
  };

  const changeStatus = async (status: InspectionStatus) => {
    setSaving(true);
    setError(null);
    const res = await whsService.updateInspection(inspectionId, { status });
    setSaving(false);
    if (res.success) await load();
    else setError(res.error.message);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={inspection ? inspection.title : 'Inspection'}
        subtitle="Conduct inspection & record results"
        icon={<ClipboardList className="h-6 w-6 text-white" />}
      />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/hr/whs/inspections')}
          className="mb-4 inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700"
        >
          <ArrowLeft className="h-4 w-4" /> Back to inspections
        </button>

        {error && (
          <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
        )}

        {loading ? (
          <p className="text-sm text-neutral-500">Loading…</p>
        ) : !inspection ? (
          <p className="text-sm text-neutral-500">Inspection not found.</p>
        ) : (
          <>
            {/* Header card */}
            <div className="rounded-xl border border-neutral-200 bg-white p-5 mb-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-3 text-sm">
                  <div>
                    <p className="text-xs text-neutral-500">Area</p>
                    <p className="font-medium text-neutral-900">{inspection.area || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">Farm</p>
                    <p className="font-medium text-neutral-900">{inspection.farm_name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">Inspector</p>
                    <p className="font-medium text-neutral-900">{inspectorName(inspection)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">Scheduled</p>
                    <p className="font-medium text-neutral-900">
                      {inspection.scheduled_date ? formatLocalDateInTz(inspection.scheduled_date, orgTz) : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">Completed</p>
                    <p className="font-medium text-neutral-900">
                      {inspection.completed_date ? formatLocalDateInTz(inspection.completed_date, orgTz) : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500">Score</p>
                    <p className="font-medium text-neutral-900">{inspection.score != null ? `${inspection.score}%` : '—'}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <Badge label={INSPECTION_STATUS_LABEL[inspection.status]} className={INSPECTION_STATUS_CLASS[inspection.status]} />
                  <select
                    value={inspection.status}
                    onChange={(e) => changeStatus(e.target.value as InspectionStatus)}
                    disabled={saving}
                    className="rounded-lg border border-neutral-300 px-2 py-1 text-xs focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>{INSPECTION_STATUS_LABEL[s]}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tally */}
              <div className="mt-4 flex flex-wrap gap-2 border-t border-neutral-100 pt-4 text-sm">
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-3 py-1 font-medium">Pass: {tally.pass}</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 px-3 py-1 font-medium">Fail: {tally.fail}</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-700 px-3 py-1 font-medium">N/A: {tally.na}</span>
              </div>
            </div>

            {/* Items */}
            {inspection.items.length === 0 ? (
              <div className="rounded-xl border border-neutral-200 bg-white p-8 text-center text-sm text-neutral-500 mb-6">
                This inspection has no checklist items.
              </div>
            ) : (
              <div className="space-y-6 mb-6">
                {grouped.map(([category, items]) => (
                  <div key={category || 'uncategorised'} className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
                    {category && (
                      <div className="bg-neutral-50 px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-neutral-500">
                        {category}
                      </div>
                    )}
                    <div className="divide-y divide-neutral-100">
                      {items.map((it) => {
                        const edit = edits[it.id] || { result: it.result, comment: it.comment || '' };
                        return (
                          <div key={it.id} className="px-5 py-4">
                            <p className="text-sm font-medium text-neutral-900 mb-2">{it.item_text}</p>
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              {RESULTS.map((r) => (
                                <button
                                  key={r}
                                  onClick={() => setResult(it.id, r)}
                                  className={`rounded-lg border px-3 py-1 text-xs font-medium transition-colors ${
                                    edit.result === r
                                      ? RESULT_ACTIVE_CLASS[r]
                                      : 'bg-white border-neutral-300 text-neutral-600 hover:bg-neutral-50'
                                  }`}
                                >
                                  {INSPECTION_RESULT_LABEL[r]}
                                </button>
                              ))}
                            </div>
                            <input
                              value={edit.comment}
                              onChange={(e) => setComment(it.id, e.target.value)}
                              placeholder="Comment (optional)"
                              className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Summary */}
            <div className="rounded-xl border border-neutral-200 bg-white p-5 mb-6">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Summary</label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
                className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                placeholder="Overall notes for this inspection…"
              />
            </div>

            {/* Actions */}
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="secondary" onClick={saveResults} isLoading={saving}>
                <Save className="h-4 w-4" /> Save results
              </Button>
              <Button
                variant="success"
                onClick={completeInspection}
                isLoading={saving}
                disabled={inspection.status === 'completed'}
              >
                <CheckCircle2 className="h-4 w-4" /> Complete inspection
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function WhsInspectionDetail() {
  return <WhsGate><WhsInspectionDetailInner /></WhsGate>;
}
